import { useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../SessionProvider';
import { getExtraJobData } from './getExtraJobData';
import { useTrackedQuery as useQuery } from '@ais-ui/performance-tracking';

/**
 * Fetches extra job data (ship data) for a completed report job.
 *
 * IMPORTANT: This hook depends on the report results cache from `useCompleteReportFetchingProcess`.
 * It will NOT fetch data until results exist in React Query cache for this jobId.
 *
 * Cache dependency: ['report', 'results', jobId]
 *
 * This prevents "DataNotLoaded" errors that occur when calling getExtraJobData
 * before the backend has finished processing the report queue.
 *
 * @see useFetchReport.ts - sets the report results cache after queue completes
 * @see useCompleteReportFetchingProcess - manages the queue polling lifecycle
 */
export const useGetExtraJobData = (
  jobId: string | undefined,
  timeZone: string,
  minLat: number,
  maxLat: number,
  minLon: number,
  maxLon: number
): UseQueryResult<Record<string, any>[]> => {
  const { dbSession } = useSession();
  const queryClient = useQueryClient();

  // Check if report results exist in cache - this indicates the queue has completed
  // and it's safe to call getExtraJobData. Without this check, we would get
  // "DataNotLoaded" errors when calling the endpoint before the queue finishes.
  const reportResults = queryClient.getQueryData(['report', 'results', jobId]);
  const isQueueComplete = reportResults !== undefined;

  return useQuery(
    {
      queryKey: ['getExtraJobData', jobId, minLat, maxLat, minLon, maxLon],
      queryFn: async () => {
        if (!dbSession) {
          throw new Error('dbSession is not defined.');
        }

        try {
          const result = await getExtraJobData(
            dbSession,
            jobId as string,
            timeZone,
            minLat,
            maxLat,
            minLon,
            maxLon
          );

          return result;
        } catch (error) {
          // Handle DataNotLoaded errors gracefully - can happen during report switching
          const errorMessage =
            error instanceof Error ? error.message : String(error);
          if (
            errorMessage.includes('DataNotLoaded') ||
            errorMessage.includes('InternalError')
          ) {
            console.warn(
              '[useGetExtraJobData] Data not ready (likely report switch):',
              errorMessage
            );
            return [];
          }
          throw error;
        }
      },
      enabled: !!jobId && isQueueComplete,
    },
    'job:extraData'
  );
};
